'use client';

import { Box, Container } from '@mui/material';
import { useAccountStore } from '@/store';
import { Notification } from '@/components/blocks/Notification/notification';
import { useCallback, useEffect, useState } from 'react';
import { IUserNotification } from '@/types/types';
import { useForbiddenPaths } from '@/hooks';
import { accountService } from '@/services';

export const NotificationLayout = () => {
    const { isAllowedToRequestAuthorizedData } = useForbiddenPaths();

    const { notifications, updateSummary } = useAccountStore((s) => ({
        notifications: s.notifications,
        updateSummary: s.updateSummary,
    }));

    const [notificationsState, setNotificationState] = useState<IUserNotification[]>();

    const filterSnoozedNotifications = useCallback((): IUserNotification[] => {
        if (typeof window === 'undefined') return [];
        const cookies: string[] = document?.cookie ? document.cookie.split('; ') : [];
        const snoozedNotificationCookies = cookies.filter((item) => item.includes('snoozed_'));
        const snoozedAssignmentIds: number[] = snoozedNotificationCookies
            .map((cookie) => {
                const cookieValueIndex = cookie.indexOf('=');
                const cookieValueExists = cookieValueIndex !== -1;
                if (cookieValueExists) {
                    return Number(cookie.substring(cookieValueIndex + 1));
                }
                return;
            })
            .filter((item): item is number => !!item);

        return notifications
            ? notifications.filter((n) => !snoozedAssignmentIds.includes(n.assignmentId))
            : [];
    }, [notifications]);

    useEffect(() => {
        if (isAllowedToRequestAuthorizedData) {
            const notificationsToShow = filterSnoozedNotifications();
            setNotificationState(notificationsToShow);
        }
    }, [notifications, isAllowedToRequestAuthorizedData, filterSnoozedNotifications]);

    const updateDisplayedNotifications = () => {
        const liveNotifications = filterSnoozedNotifications();
        setNotificationState(liveNotifications);
    };

    const updateSummaryAndNotifications = async () => {
        const summary = await accountService.summary();
        updateSummary(summary);
        updateDisplayedNotifications();
    };

    if (!isAllowedToRequestAuthorizedData || !notificationsState) return null;

    return (
        <Container sx={{ my: 4 }}>
            {notificationsState.map((notification) => {
                return (
                    <Box sx={{ my: 2 }} key={notification.assignmentId}>
                        <Notification
                            notification={notification}
                            removeSnoozedNotification={updateDisplayedNotifications}
                            removeDismissedNotification={updateSummaryAndNotifications}
                        />
                    </Box>
                );
            })}
        </Container>
    );
};
